/**
 * GoalsViewToggle (Epic 18 — /goals)
 *
 * The Active ⇄ History view switch, styled to sit EXACTLY like the clients
 * page's "Show Archived" button (variant outline, the same className string +
 * the same inline 0.5px border). It is a single flip button — like
 * Show/Hide Archived — whose label is the DESTINATION view:
 *   - on the Active view it reads "History" and switches to history,
 *   - on the History view it reads "Active" and switches back.
 *
 * Extracted to its own component so it can live both inside GoalsFilterToolbar
 * (active view) AND right-aligned above GoalHistory (history view) — the
 * GoalsFilterToolbar only renders in the active view, so the History view needs
 * its own copy of the toggle to get back. The button's data-testid is the
 * destination view (goals-view-active / goals-view-history).
 */

import { Button } from "@provenleap/ui";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import type { GoalsView } from "../../routes/_authenticated/goals/index";

export interface GoalsViewToggleProps {
  activeView: GoalsView;
  onViewChange: (view: GoalsView) => void;
}

export function GoalsViewToggle({ activeView, onViewChange }: GoalsViewToggleProps) {
  // The button flips like Show/Hide Archived: clicking on Active goes to
  // History, clicking on History goes back to Active.
  const target: GoalsView = activeView === "active" ? "history" : "active";
  return (
    <Button
      variant="outline"
      onClick={() => onViewChange(target)}
      className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
      style={{ border: "0.5px solid var(--hairline)" }}
      data-testid={`goals-view-${target}`}
    >
      <ClockCounterClockwise size={16} className="mr-1" />
      {activeView === "active" ? "History" : "Active"}
    </Button>
  );
}
